import { useState, type CSSProperties, type SVGProps } from "react";
import {
  CATEGORY_LABELS,
  type CategoryId,
  type CategoryMetric,
  type HistogramBucket,
  type ServiceMetric,
} from "../../shared/model";
import { formatMxn } from "../../shared/metrics";
import { ServiceLogo } from "./ServiceLogo";

interface HistogramProps {
  buckets: HistogramBucket[];
  meanMonthlyCents: number;
  medianMonthlyCents: number;
  responseCount: number;
}

function bucketContains(bucket: HistogramBucket, cents: number) {
  return cents >= bucket.minCents && (bucket.maxCents === null || cents <= bucket.maxCents);
}

function MetricFlag({ label }: { label: "Media" | "Mediana" }) {
  return <span className={`skyline-flag skyline-flag--${label.toLowerCase()}`}>{label}</span>;
}

export function Histogram({
  buckets,
  meanMonthlyCents,
  medianMonthlyCents,
  responseCount,
}: HistogramProps) {
  const max = Math.max(...buckets.map((bucket) => bucket.count), 1);

  return (
    <div
      className="skyline"
      role="img"
      aria-label={`Distribución agregada de ${responseCount} ${
        responseCount === 1 ? "respuesta" : "respuestas"
      } por rango de gasto mensual`}
    >
      {buckets.map((bucket, index) => {
        const height = bucket.count === 0 ? 2 : Math.max((bucket.count / max) * 100, 14);
        const hasMean = responseCount > 0 && bucketContains(bucket, meanMonthlyCents);
        const hasMedian = responseCount > 0 && bucketContains(bucket, medianMonthlyCents);

        return (
          <div className="skyline-column" key={bucket.id}>
            <div className="skyline-flags" aria-hidden="true">
              {hasMean && <MetricFlag label="Media" />}
              {hasMedian && <MetricFlag label="Mediana" />}
            </div>
            <strong className="skyline-count">{bucket.count}</strong>
            <div
              className={`skyline-building skyline-building--${(index % 3) + 1}`}
              style={{ "--building-height": `${height}%` } as CSSProperties}
            >
              <span className="skyline-roof" />
            </div>
            <small>{bucket.label}</small>
          </div>
        );
      })}
      <div className="skyline-ground" aria-hidden="true" />
    </div>
  );
}

export function ServiceRanking({ services }: { services: ServiceMetric[] }) {
  const [mode, setMode] = useState<"adoption" | "spend">("adoption");
  const sorted = [...services]
    .sort((a, b) => {
      const difference =
        mode === "adoption"
          ? b.count - a.count
          : b.monthlySpendCents - a.monthlySpendCents;
      return difference || a.name.localeCompare(b.name, "es");
    })
    .slice(0, 5);
  const max = Math.max(
    ...sorted.map((service) => (mode === "adoption" ? service.count : service.monthlySpendCents)),
    1,
  );
  const totalSpend = services.reduce((total, service) => total + service.monthlySpendCents, 0);

  return (
    <div className="service-race">
      <div className="segmented-control ranking-control" aria-label="Orden del ranking">
        <button
          aria-pressed={mode === "adoption"}
          className={mode === "adoption" ? "is-active" : ""}
          onClick={() => setMode("adoption")}
          type="button"
        >
          Más elegidos
        </button>
        <button
          aria-pressed={mode === "spend"}
          className={mode === "spend" ? "is-active" : ""}
          onClick={() => setMode("spend")}
          type="button"
        >
          Mayor gasto
        </button>
      </div>
      <ol className={`ranking ranking--${mode}`} aria-live="polite">
        {sorted.map((service, index) => {
          const value = mode === "adoption" ? service.count : service.monthlySpendCents;
          const percentage =
            mode === "adoption"
              ? Math.round(service.adoptionRate * 100)
              : totalSpend === 0
                ? 0
                : Math.round((service.monthlySpendCents / totalSpend) * 100);

          return (
            <li key={service.serviceId}>
              <span className="ranking-position">{String(index + 1).padStart(2, "0")}</span>
              <ServiceLogo className="ranking-logo" serviceId={service.serviceId} />
              <div className="ranking-body">
                <div className="ranking-copy">
                  <strong>{service.name}</strong>
                  <span>
                    {mode === "adoption"
                      ? `${service.count} ${service.count === 1 ? "persona" : "personas"}`
                      : `${formatMxn(service.monthlySpendCents)} / mes`}
                  </span>
                </div>
                <strong className="ranking-percentage">{percentage}%</strong>
                <span
                  className="ranking-bar"
                  style={{ "--bar-width": `${value === 0 ? 0 : Math.max((value / max) * 100, 3)}%` } as CSSProperties}
                />
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

type IconProps = SVGProps<SVGSVGElement>;

function MusicIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 64 64" {...props}>
      <path d="M18 39V27a14 14 0 0 1 28 0v12" />
      <path d="M18 36h-4a7 7 0 0 0-7 7v5a7 7 0 0 0 7 7h4V36Zm28 0h4a7 7 0 0 1 7 7v5a7 7 0 0 1-7 7h-4V36Z" />
    </svg>
  );
}

function VideoIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 64 64" {...props}>
      <rect x="6" y="11" width="52" height="38" />
      <path d="m27 22 14 8-14 8V22Z" />
      <path d="M23 57h18M32 49v8" />
    </svg>
  );
}

function ProductivityIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 64 64" {...props}>
      <rect x="10" y="10" width="36" height="44" />
      <path d="m17 23 4 4 8-9M17 39l4 4 8-9M33 25h7M33 41h7M51 7v10M46 12h10" />
    </svg>
  );
}

function CloudIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 64 64" {...props}>
      <path d="M19 49h31a10 10 0 0 0 1-20 17 17 0 0 0-32-5 13 13 0 0 0 0 25Z" />
    </svg>
  );
}

function GamesIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 64 64" {...props}>
      <path d="M20 22h24c8 0 14 8 13 17l-1 8c-1 7-9 9-13 3l-4-6H25l-4 6c-4 6-12 4-13-3l-1-8c-1-9 5-17 13-17Z" />
      <path d="M16 34h12M22 28v12M43 32h.1M50 38h.1" />
    </svg>
  );
}

export function CategoryIcon({ category }: { category: CategoryId }) {
  const props: IconProps = {
    "aria-hidden": true,
    className: "category-icon-svg",
    fill: "none",
    stroke: "currentColor",
    strokeLinecap: "square",
    strokeLinejoin: "miter",
    strokeWidth: 4,
  };

  if (category === "musica") return <MusicIcon {...props} />;
  if (category === "video") return <VideoIcon {...props} />;
  if (category === "productividad") return <ProductivityIcon {...props} />;
  if (category === "nube") return <CloudIcon {...props} />;
  return <GamesIcon {...props} />;
}

export function CategoryBars({ categories }: { categories: CategoryMetric[] }) {
  const totalSpend = categories.reduce((total, category) => total + category.monthlySpendCents, 0);
  const sorted = [...categories].sort(
    (a, b) => b.monthlySpendCents - a.monthlySpendCents || b.count - a.count,
  );
  const winner = totalSpend > 0 ? sorted[0]?.category : null;

  return (
    <div className="category-lanes">
      {sorted.map((category) => {
        const percentage =
          totalSpend === 0 ? 0 : Math.round((category.monthlySpendCents / totalSpend) * 100);
        const isWinner = category.category === winner;

        return (
          <article
            className={`category-lane ${isWinner ? "is-winner" : ""}`}
            key={category.category}
          >
            <span className="category-icon">
              <CategoryIcon category={category.category} />
            </span>
            <div className="category-lane-body">
              <div className="category-lane-copy">
                <strong>{CATEGORY_LABELS[category.category]}</strong>
                <span>
                  {category.count} {category.count === 1 ? "selección" : "selecciones"} ·{" "}
                  {percentage}% del gasto
                </span>
              </div>
              <strong className="category-value">{formatMxn(category.monthlySpendCents)}</strong>
              <div className="category-track">
                <span style={{ "--bar-width": `${percentage}%` } as CSSProperties} />
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}
